import React, { useState, useContext, useRef, useMemo } from 'react'
import { Menu, X, Lock } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { DashboardContext } from '@/context/DashboardContext.jsx'
import { AuthContext } from '@/context/AuthContext.jsx'
import { useCustomFetch } from '@/hooks/useCustomFetch'
import useOutsideClick from '@/hooks/useOutsideClick'
import evermuseLogo from '@/assets/evermuse-logo-15h.svg'
import { TeamSettingsIcon } from '@/components/icons/Settings'
import { ProductsIcon } from '@/components/icons/Products'
import { CompetitorsIcon } from '@/components/icons/Competitors'
import { IntegrationsIcon } from '@/components/icons/Integrations'
import { TeamIcon } from '@/components/icons/Team'

// removed meeting actions from hamburger menu

interface HamburgerMenuProps {
  selectedMenu: string
  isOnboarding?: boolean
}

export function HamburgerMenu({ selectedMenu, isOnboarding = false }: HamburgerMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [openWorkspaceMenu, setOpenWorkspaceMenu] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  // meeting action state removed from hamburger menu
  const navigate = useNavigate()
  const { user } = useContext(AuthContext) as any
  const { workspaces, activeWorkspace } = useContext(DashboardContext) as any
  const customFetch = useCustomFetch()
  const workspaceSelectorRef = useRef(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const settingsItem = {
    id: 'team-settings',
    label: 'Settings',
    icon: <TeamSettingsIcon isSelected={selectedMenu === 'team-settings'} />,
  }

  const subMenuItems = [
    {
      id: 'products',
      label: 'Products',
      icon: <ProductsIcon isSelected={selectedMenu === 'products'} />,
    },
    {
      id: 'competitors',
      label: 'Competitors',
      icon: <CompetitorsIcon isSelected={selectedMenu === 'competitors'} />,
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: <IntegrationsIcon isSelected={selectedMenu === 'integrations'} />,
    },
    {
      id: 'team',
      label: 'Team',
      icon: <TeamIcon isSelected={selectedMenu === 'team'} />,
    },
  ]

  const handleMenuClick = (menuId: string) => {
    navigate(`/dashboard/${menuId}`)
    setIsOpen(false)
  }

  const handleChangeActiveWorkspace = async (newActiveWorkspace: string) => {
    if (user.active_workspace === newActiveWorkspace) return
    try {
      await customFetch('/updateActiveWorkspace', 'PUT', { workspaceId: newActiveWorkspace })
      window.location.reload()
    } catch (err) {
      console.error(err)
    }
  }

  // meeting action handlers removed from hamburger menu

  // Usage calculation
  const percentage = useMemo(() => {
    if (!activeWorkspace) return 0
    const percentage = Math.round(
      100 -
        ((activeWorkspace?.additional_amount + activeWorkspace?.sub_hours_left) * 100) / activeWorkspace?.total_amount
    )
    if (percentage < 0) return 0
    if (percentage > 100) return 100
    return percentage
  }, [activeWorkspace?.additional_amount, activeWorkspace?.sub_hours_left, activeWorkspace?.total_amount])

  const timeLeft = useMemo(() => {
    if (!activeWorkspace) return 0
    if (activeWorkspace.additional_amount + activeWorkspace.sub_hours_left < 0) return 0
    const hours = (activeWorkspace.additional_amount + activeWorkspace.sub_hours_left) / 60
    if (Number.isInteger(hours)) {
      return hours
    } else if ((hours * 10) % 1 === 0) {
      return Number(hours.toFixed(1))
    }
    return Number(hours.toFixed(2))
  }, [activeWorkspace?.additional_amount, activeWorkspace?.sub_hours_left])

  // Filter workspaces by search query
  const filteredWorkspaces = useMemo(() => {
    const workspaceKeys = Object.keys(user?.workspaces || {}).filter((workspace) => Boolean(workspaces[workspace]))
    if (!searchQuery.trim()) {
      return workspaceKeys
    }
    return workspaceKeys.filter((workspace) =>
      workspaces[workspace].name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [searchQuery, user?.workspaces, workspaces])

  useOutsideClick(menuRef, () => {
    setIsOpen(false)
  })

  useOutsideClick(workspaceSelectorRef, () => {
    setOpenWorkspaceMenu(false)
    setSearchQuery('')
  })

  // Focus search input when workspace menu opens
  React.useEffect(() => {
    if (openWorkspaceMenu && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100)
    }
  }, [openWorkspaceMenu])

  const menuPortal = isOpen
    ? createPortal(
        <div>
          <div className="fixed inset-0 bg-black/50 z-[1000]" onClick={() => setIsOpen(false)} />
          <div
            ref={menuRef}
            className={`fixed top-0 left-0 right-auto bottom-0 h-[100svh] w-80 bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out z-[1001] flex flex-col ${
              isOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            {/* Header with Logo */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <img src={evermuseLogo} alt="Evermuse" width="124" height="15" />
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
              {/* Workspace Selector */}
              <div className="relative" ref={workspaceSelectorRef}>
                {workspaces && Object.keys(workspaces).length > 0 && user?.active_workspace ? (
                  <>
                    <button
                      className="w-full p-2 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 flex items-center justify-between"
                      onClick={() => setOpenWorkspaceMenu(!openWorkspaceMenu)}
                    >
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                        {workspaces[user.active_workspace]?.name || 'Select Workspace'}
                      </span>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_workspace_mobile)">
                          <circle cx="10" cy="10" r="7.5" fill="#EBEBEB" className="dark:fill-gray-600" />
                          <path
                            d="M12.5 9.16602L10 11.666L7.5 9.16602"
                            stroke="#40424A"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="dark:stroke-gray-300"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_workspace_mobile">
                            <rect width="20" height="20" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                    {openWorkspaceMenu && (
                      <div
                        className="absolute w-full left-0 top-12 p-2 bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 z-10 flex flex-col"
                        style={{ maxHeight: 400 }}
                      >
                        {/* Search Input */}
                        <div className="mb-2">
                          <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search workspaces..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#FF4A8E]"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>

                        {/* Scrollable workspace list */}
                        <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
                          {filteredWorkspaces.length > 0 ? (
                            filteredWorkspaces.map((workspace) => (
                              <button
                                key={workspace}
                                className="w-full p-2 flex items-center gap-2 my-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                                onClick={() => handleChangeActiveWorkspace(workspace)}
                              >
                                {user.active_workspace === workspace ? (
                                  <>
                                    <svg
                                      width="17"
                                      height="12"
                                      viewBox="0 0 17 12"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M1 6L6 11L16 1"
                                        stroke="#FF4A8E"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    <span className="text-sm font-bold text-gray-800 dark:text-white">
                                      {workspaces[workspace].name}
                                    </span>
                                  </>
                                ) : (
                                  <span className="ml-6 text-sm font-medium text-gray-600 dark:text-gray-300">
                                    {workspaces[workspace].name}
                                  </span>
                                )}
                              </button>
                            ))
                          ) : (
                            <div className="px-2 py-4 text-center">
                              <p className="text-sm text-gray-500 dark:text-gray-400">No workspaces found</p>
                            </div>
                          )}
                        </div>

                        {/* Add Workspace button */}
                        {(Object.values(workspaces).some((item: any) => item.hasOwnProperty('plan_id')) ||
                          Object.values(workspaces).length < 1) && (
                          <button
                            className="w-full mt-2 pt-2 pb-1 border-t border-gray-200 dark:border-gray-700 flex items-center justify-center gap-2"
                            onClick={() => {
                              navigate('/workspace/create')
                              setIsOpen(false)
                            }}
                          >
                            <svg
                              width="13"
                              height="13"
                              viewBox="0 0 13 13"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g clipPath="url(#clip0_add_workspace_mobile)">
                                <path
                                  d="M6.5 2.70898V10.2923"
                                  stroke="#FF4A8E"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M2.70898 6.5H10.2923"
                                  stroke="#FF4A8E"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_add_workspace_mobile">
                                  <rect width="13" height="13" fill="white" />
                                </clipPath>
                              </defs>
                            </svg>
                            <span className="text-sm font-medium text-gray-800 dark:text-white">Add Workspace</span>
                          </button>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full p-2 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600">
                    <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Loading...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Scrollable Navigation */}
            <nav className="flex-1 overflow-y-auto p-4">
              {/* Top-level Settings */}
              <div className="relative">
                <button
                  onClick={isOnboarding ? undefined : () => handleMenuClick(settingsItem.id)}
                  disabled={isOnboarding}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isOnboarding
                      ? 'opacity-40 cursor-not-allowed text-gray-400 dark:text-gray-600'
                      : selectedMenu === settingsItem.id
                        ? 'bg-pink-50 dark:bg-pink-900/20 text-[#FF4A8E]'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <div className="flex-shrink-0">{settingsItem.icon}</div>
                  <span className="font-medium">{settingsItem.label}</span>
                  {isOnboarding && <Lock className="w-4 h-4 ml-auto text-gray-400" />}
                </button>
              </div>

              {/* Indented sub-items under Settings */}
              <div className="mt-1 ml-8 border-l border-gray-200 dark:border-gray-700">
                <div className="pl-3">
                  {subMenuItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={isOnboarding ? undefined : () => handleMenuClick(item.id)}
                      disabled={isOnboarding}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                        isOnboarding
                          ? 'opacity-40 cursor-not-allowed text-gray-400 dark:text-gray-600'
                          : selectedMenu === item.id
                            ? 'bg-pink-50 dark:bg-pink-900/20 text-[#FF4A8E]'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex-shrink-0">{item.icon}</div>
                      <span className="text-[15px]">{item.label}</span>
                      {isOnboarding && <Lock className="w-4 h-4 ml-auto text-gray-400" />}
                    </button>
                  ))}
                </div>
              </div>
            </nav>

            {/* Usage Section at Bottom */}
            <div className="border-t border-gray-200 dark:border-gray-700 p-4 pb-[calc(env(safe-area-inset-bottom,0px)+16px)]">
              <div className="mb-3">
                <p className="text-xs font-extrabold uppercase text-gray-500 dark:text-gray-400 tracking-wide mb-2">
                  Workspace Usage
                </p>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-gray-800 dark:text-white">{percentage || 0}%</span>
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    <span className="font-bold">{timeLeft || 0}</span> hours remaining
                  </span>
                </div>
                <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    style={{ width: `${percentage}%` }}
                    className="h-full bg-[#FF4A8E] rounded-full transition-all duration-300"
                  />
                </div>
                {activeWorkspace?.sub_extra_hour_charge ? (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Overage:{' '}
                    <span className="font-semibold">${activeWorkspace.sub_extra_hour_charge.toFixed(2)}/hr</span>
                  </p>
                ) : (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {activeWorkspace?.additional_amount
                      ? 'AI functions will pause when free hours run out.'
                      : 'Upgrade to continue using AI features.'}
                  </p>
                )}
              </div>
              <button
                className="w-full py-2 px-3 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium"
                onClick={() => window.open('https://www.evermuse.com/pricing/', '_blank')}
              >
                Upgrade Plan
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null

  return (
    <div>
      {/* Hamburger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="Toggle menu"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
        ) : (
          <Menu className="w-6 h-6 text-gray-600 dark:text-gray-400" />
        )}
      </button>

      {/* Portal for overlay and menu to escape parent stacking contexts */}
      {menuPortal as any}
    </div>
  )
}
